import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import OtpInput from "components/Otp";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import InfoBanner from "components/InfoBanner";
import LinkButton from "components/LinkButton";

import { finishPasswordlessLogin } from "services/auth.service";
import { setUserData, getFlowState, clearFlowState, setJwtTokens } from "../../storage";
import { useLoginPage } from "hooks/useLoginPage";
import { useText } from "context/TextProvider";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsFlow, AnalyticsMethod, AnalyticsStage, AnalyticsStatus } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";
import { postAuthTokenToNative } from "utils/nativeBridge";

const OtpVerification = (): JSX.Element => {
  const text = useText();
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_OTP_SCREEN);
  const flowState = getFlowState(configId);
  const email = flowState.email || "";
  const code = flowState.code || "";
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const enteredOtpScreenAt = useRef(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    trackEvent(AnalyticsEvent.TIME_TO_ENTER_OTP, { duration_ms: Date.now() - enteredOtpScreenAt.current });
    try {
      const data = await finishPasswordlessLogin({
        code,
        oneTimeCode: otp,
        url: loginPageConfig?.urls?.submit_login_otp,
      });
      setUserData(data);
      const { user, ...tokens } = data;
      setJwtTokens(tokens);
      setMessage(text.otpVerification.verifiedSuccess);
      trackEvent(AnalyticsEvent.PASSWORDLESS_LOGIN_SUCCEEDED, { method: AnalyticsMethod.PASSWORDLESS, flow: AnalyticsFlow.STANDARD, status: AnalyticsStatus.SUCCEEDED });
      if (flowState.loginStartedAt) {
        trackEvent(AnalyticsEvent.TIME_TO_LOGIN, { duration_ms: Date.now() - flowState.loginStartedAt });
      }
      const jwt = data.token;
      if (data.profileUpdate?.showUpdateScreen) {
        clearFlowState(configId);
        appNavigate(`/profile-update`, {state: { email } });
        return;
      }
      clearFlowState(configId);
      postAuthTokenToNative({
        token: jwt,
        refreshToken: data.refreshToken,
        tokenExpirationInstant: data.tokenExpirationInstant,
        refreshUrl: loginPageConfig?.urls?.refresh_url,
      });
      appNavigate(`/`);
    } catch (err) {
      setMessage(text.otpVerification.verificationFailed);
      trackEvent(AnalyticsEvent.PASSWORDLESS_LOGIN_FAILED, {
        method: AnalyticsMethod.PASSWORDLESS,
        flow: AnalyticsFlow.STANDARD,
        status: AnalyticsStatus.FAILED,
        stage: AnalyticsStage.OTP,
      });
      trackEvent(AnalyticsEvent.OTP_ERROR, { message: err instanceof Error ? err.message : undefined });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta name="OTP Verification" />
      <PageTitle>{text.otpVerification.title}</PageTitle>
      <InfoBanner>
        <Typography component="div" sx={{ fontWeight: 500, mb: "0.25rem" }}>
          {text.otpVerification.codeSentPrefix}{" "}
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {email}
          </Typography>
          .
        </Typography>
        <Typography component="div" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          {text.otpVerification.checkSpamFolder}
        </Typography>
      </InfoBanner>
      <OtpInput otp={otp} setOtp={setOtp} />
      <LoadingButton
        loading={loading}
        onClick={handleSubmit}
        disabled={otp.length !== 6}
        sx={{
          width: "100%",
          height: "3.5rem",
          mb: "1.5rem",
        }}
      >
        {text.otpVerification.submitButton}
      </LoadingButton>
      {message && <ErrorMessage sx={{ mb: "1.5rem" }}>{message}</ErrorMessage>}
      <Box sx={{ textAlign: "center" }}>
        <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
          {text.otpVerification.troubleLoggingIn}
        </Typography>
        <LinkButton
          onClick={() => appNavigate(`/password-login`)}
          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
        >
          {text.otpVerification.loginWithPasswordButton}
        </LinkButton>
      </Box>
    </>
  );
};

export default OtpVerification;
